import { NotificationProgrammatic as Notification } from "buefy";

/**
 * Devuelve el `detail` de un error de DRF si vino en la respuesta, o el
 * fallback provisto. Pensado para callers (acciones Vuex, vistas) que
 * setean el mensaje de error en el estado para mostrarlo inline. NO
 * dispara notification ni throw — para ese flujo usar `consoleHandlerError`.
 */
export function extractErrorMessage(error, fallback) {
    return error?.response?.data?.detail ?? fallback;
}

/**
 * True para cancelaciones de axios (AbortController). No son errores: son el
 * control de tráfico esperado de `createTakeLatest`, que aborta el request en
 * vuelo cuando uno más nuevo lo reemplaza (cambio rápido de filtros, cambio
 * de vista clasificador ↔ Libro Mayor). Mismo criterio que `axios.isCancel`
 * sin importar axios acá.
 */
export function isCanceledError(error) {
    return Boolean(
        error &&
        (error.code === "ERR_CANCELED" ||
            error.name === "CanceledError" ||
            error.__CANCEL__),
    );
}

export function consoleHandlerError({
    error = null,
    refFields = Object,
    customDefaultMessage = null,
    empty = false,
    indefinite = false,
    // Opt-in opcional. Cuando `silent` es true, suprime la notificación
    // visual y el re-throw — pensado para callers (ej. acciones Vuex) que ya
    // gestionan el error vía estado (`commit("SET_ERROR", ...)`) y no quieren
    // duplicar UX ni rechazar la promesa. El default mantiene el
    // comportamiento histórico para no romper consumidores existentes.
    silent = false,
    // Prefijo opcional para los logs (ej. "[salesReport]"). Útil para
    // identificar de qué módulo proviene un error en la consola del browser.
    prefix = "",
}) {
    // Cancelación intencional (take-latest abortó un request superado): ni
    // log ni notificación ni re-throw — tratarla como error sólo mete ruido.
    if (isCanceledError(error)) return;
    const tag = prefix ? `${prefix} ` : "";
    console.error(`${tag}${JSON.stringify(error)}`);
    console.error(
        `${tag}${JSON.stringify(error?.response?.data) || "No hay detalle del error"}`
    );
    console.error(`${tag}${error?.response?.status || "Sin retorno del estado"}`);
    console.error(`${tag}${error?.response?.statusText || "Sin detalle del estado"}`);
    if (silent) return;
    let msg = empty ? null : error?.response?.data || null;
    showErrorMessage(msg, refFields, customDefaultMessage, indefinite);
    throw error;
}

export function notificate(
    message,
    type,
    position = "is-top-right",
    indefinite = false,
    duration = 5000
) {
    Notification.open({
        message: message,
        type: type,
        position: position,
        ...(!indefinite && {
            duration: duration,
        }),
        ...(indefinite && {
            indefinite: indefinite,
        }),
    });
}

function showErrorMessage(
    innerMessage = null,
    refFields = Object,
    customDefaultMessage = null,
    indefinite = false,
) {
    let message = "";
    if (isObject(innerMessage)) {
        let additionalKeys = ["counterpart", "owner", "company"];
        let expectedKeys = Object.keys(refFields).concat(additionalKeys);
        Object.entries(innerMessage).forEach(function([key, value]) {
            if (expectedKeys.includes(key)) {
                message = setFormError(
                    refFields,
                    key,
                    value,
                    customDefaultMessage
                );
            } else {
                let firstMessage = Array.isArray(value) ? value[0] : value;
                message += showErrorMessage(
                    firstMessage, refFields, null, indefinite
                );
            }
        });
        return message;
    } else {
        return notificateInnerMessageOrDefault(
            innerMessage,
            customDefaultMessage,
            indefinite,
        );
    }
}

function setFormError(
    refFields,
    refName,
    errorMessage,
    customDefaultMessage = null
) {
    if (isObject(errorMessage) && !Array.isArray(errorMessage)) {
        Object.entries(errorMessage).forEach(function([key, value]) {
            let fieldName = `${refName}_${key}`;
            return setFormError(
                refFields,
                fieldName,
                value,
                customDefaultMessage
            );
        });
    } else {
        return fillFormErrorElementOrDefault(
            refFields,
            refName,
            errorMessage,
            customDefaultMessage
        );
    }
}

function fillFormErrorElementOrDefault(
    refFields,
    refName,
    message,
    defaultMessage = null
) {
    let msg = "";
    if (refFields[refName] && refFields[refName].$children) {
        let msg = defaultMessage || message;
        refFields[refName].$children[0].setErrors(msg);
    } else {
        let field = translateFieldName(refName);
        msg = notificateInnerMessageOrDefault(`${field}: ${message}`);
    }
    return msg;
}

function notificateInnerMessageOrDefault(
    message = null,
    customDefaultMessage = null,
    indefinite = false,
) {
    let defaultMessage = `
        <p class="m-0">
            <strong>Lo sentimos, estamos teniendo un pequeño error.</strong>
        </p><br>
        <p class="m-0">Ya estamos trabajando para mejorar la situación y
            nos tomará un momento para solucionarlo.
        </p>
    `;
    let msg = customDefaultMessage || message || defaultMessage;
    if (msg.includes("Server Error (500)")) {
        msg = defaultMessage;
    }
    notificate(`<p class="m-0">${msg}</p>`, "is-danger is-light", "is-top-right", indefinite);
}

function translateFieldName(fieldName) {
    return {
        financial_entity: "Institución Financiera",
        amount: "Monto",
    }[fieldName];
}

function isObject(param) {
    return typeof param === "object" && param !== null;
}
